/**
 * Merge LLM final payload fields into a single POST /v1/flights/timeline body.
 */

import type { FlightTimelineBuildRequestPayload, LlmGenerateResponse } from '../types/chat';

export function mergeFlightTimelineRequest(
  llm: LlmGenerateResponse,
  sessionId?: string | null
): (FlightTimelineBuildRequestPayload & { session_id?: string }) | null {
  const action = llm.ui_actions?.find((a) => a.type === 'render_flight_timeline');
  const fromAction = action?.type === 'render_flight_timeline' ? action.payload : undefined;
  const base = llm.flight_timeline_payload;

  if (llm.show_timeline === false && fromAction == null) {
    return null;
  }

  const wantsTimeline =
    fromAction != null || llm.show_timeline === true || base?.show_timeline === true;

  if (!wantsTimeline) {
    return null;
  }

  const merged: FlightTimelineBuildRequestPayload = {
    show_timeline: true,
    ...base,
    ...fromAction,
  };

  if (typeof llm.show_timeline === 'boolean') {
    merged.show_timeline = llm.show_timeline;
  }

  return {
    ...merged,
    session_id: sessionId || undefined,
  };
}

/** Normalize arbitrary positive weights to percentages summing to 100. */
export function normalizeChannelPercents(weights: Record<string, number>): Record<string, number> {
  const entries = Object.entries(weights).filter(([, v]) => typeof v === 'number' && v >= 0);
  const sum = entries.reduce((acc, [, v]) => acc + v, 0);
  if (sum <= 0) {
    const n = entries.length || 1;
    const even = 100 / n;
    return Object.fromEntries(entries.map(([k]) => [k, Math.round(even * 100) / 100]));
  }
  const out: Record<string, number> = {};
  let running = 0;
  entries.forEach(([k, v], i) => {
    if (i === entries.length - 1) {
      out[k] = Math.round((100 - running) * 100) / 100;
    } else {
      const p = (v / sum) * 100;
      const rounded = Math.round(p * 100) / 100;
      out[k] = rounded;
      running += rounded;
    }
  });
  return out;
}
